import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, Save, RotateCcw, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const FIELDS = [
  { key: "subject", label: "Subiectul emailului", hint: "{title} se înlocuiește cu titlul însemnării alese" },
  { key: "kicker", label: "Rândul mic din antet" },
  { key: "headline", label: "Fraza de deschidere", rows: 2 },
  { key: "note", label: "Un gând personal (opțional)", rows: 4, hint: "Apare sub fraza de deschidere. Paragrafe separate printr-un rând gol." },
  { key: "label", label: "Eticheta de deasupra însemnării" },
  { key: "others_label", label: "Titlul listei cu celelalte însemnări" },
  { key: "closing", label: "Fraza de încheiere", rows: 2 },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("ro-RO", { weekday: "long", day: "numeric", month: "long" });
  } catch {
    return iso;
  }
};

export const AdminNewsletterPage = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [postId, setPostId] = useState(null);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const { data: d } = await api.get("/admin/newsletter/preview");
    setData(d);
    setForm(Object.fromEntries(Object.keys(d.defaults).map((k) => [k, d.draft?.[k] || ""])));
    setPostId(d.draft?.post_id || null);
  };

  useEffect(() => {
    load().catch((e) => toast.error(formatApiError(e, "Nu am putut încărca previzualizarea")));
  }, []);

  const save = async () => {
    setBusy("save");
    try {
      await api.put("/admin/newsletter/draft", { ...form, post_id: postId });
      await load();
      toast.success("Am salvat — așa va pleca emailul de vineri.");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut salva"));
    } finally {
      setBusy("");
    }
  };

  const reset = async () => {
    setBusy("reset");
    try {
      await api.delete("/admin/newsletter/draft");
      await load();
      toast.success("Am revenit la textele implicite și la alegerea automată.");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut reseta"));
    } finally {
      setBusy("");
    }
  };

  const sendNow = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Trimit emailul acum către ${data.subscribers} abonați?`)) return;
    setBusy("send");
    try {
      const { data: r } = await api.post("/admin/newsletter/send-now");
      toast.success(r.sent ? `Trimis către ${r.sent} abonați.` : "Nu s-a trimis nimic (fără cheie Resend sau fără însemnări noi).");
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut trimite"));
    } finally {
      setBusy("");
    }
  };

  if (!data) {
    return <p className="py-24 text-center text-sm text-[#F7F1E8]/68" data-testid="newsletter-loading">Pregătesc previzualizarea…</p>;
  }

  return (
    <main data-testid="admin-newsletter" className="mx-auto max-w-6xl px-6 py-12">
      <Link to="/admin" data-testid="newsletter-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]">
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>
      <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">Newsletter</p>
          <h1 className="mt-2 font-serif text-4xl font-light text-[#F7F1E8]">Emailul de vineri</h1>
          <p className="mt-2 text-sm text-[#F7F1E8]/78" data-testid="newsletter-schedule">
            Pleacă <strong>{fmtDate(data.send_on)}</strong>, ora {data.send_hour}:00, către {data.subscribers} {data.subscribers === 1 ? "abonat" : "abonați"}.
            {!data.resend_configured && " Atenție: cheia Resend nu e configurată — emailul nu va putea pleca."}
          </p>
        </div>
        <button
          type="button"
          onClick={sendNow}
          disabled={!!busy || !data.has_posts}
          data-testid="newsletter-send-now-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-5 py-3 text-xs font-medium uppercase tracking-[0.2em] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-50"
        >
          {busy === "send" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" strokeWidth={1.6} />}
          Trimite acum
        </button>
      </div>

      {!data.has_posts ? (
        <div data-testid="newsletter-empty" className="mt-10 rounded-3xl bg-[var(--tint-butter)] p-12 text-center">
          <Mail className="mx-auto h-8 w-8 text-[var(--sun-dark)]" strokeWidth={1.4} />
          <p className="mt-4 font-serif text-2xl text-[#F7F1E8]">Nicio însemnare nouă în ultimele 7 zile.</p>
          <p className="mt-2 text-sm text-[#F7F1E8]/78">Vineri nu va pleca niciun email. Publică ceva săptămâna asta și previzualizarea apare aici.</p>
        </div>
      ) : (
        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="space-y-6 lg:col-span-5">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Însemnarea recomandată</p>
              <ul className="mt-3 space-y-2" data-testid="newsletter-post-list">
                {data.posts.map((p) => {
                  const active = (postId || data.auto_id) === p.id;
                  return (
                    <li key={p.id}>
                      <label className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border px-4 py-3 transition-colors ${active ? "border-[var(--accent)] bg-[var(--tint-pink)]" : "border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] hover:bg-[var(--tint-butter)]"}`}>
                        <span className="flex items-center gap-3">
                          <input type="radio" name="newsletter-post" checked={active} onChange={() => setPostId(p.id)} data-testid={`newsletter-post-radio-${p.id}`} className="accent-[var(--accent)]" />
                          <span className="font-serif text-[15px] text-[#F7F1E8]">{p.title}</span>
                        </span>
                        <span className="shrink-0 text-[11px] uppercase tracking-[0.15em] text-[#F7F1E8]/68">
                          {p.visits} {p.visits === 1 ? "vizită" : "vizite"}{p.id === data.auto_id ? " · cea mai puțin citită" : ""}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>

            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">{f.label}</label>
                {f.rows ? (
                  <textarea
                    rows={f.rows}
                    value={form[f.key] || ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                    placeholder={data.defaults[f.key]}
                    data-testid={`newsletter-field-${f.key}`}
                    className="mt-2 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
                  />
                ) : (
                  <input
                    value={form[f.key] || ""}
                    onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                    placeholder={data.defaults[f.key]}
                    data-testid={`newsletter-field-${f.key}`}
                    className="mt-2 w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
                  />
                )}
                {f.hint && <p className="mt-1 text-xs text-[#F7F1E8]/62">{f.hint}</p>}
              </div>
            ))}

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                type="button"
                onClick={save}
                disabled={!!busy}
                data-testid="newsletter-save-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--accent)] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-white transition-transform hover:-translate-y-0.5 active:scale-95 disabled:opacity-50"
              >
                {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" strokeWidth={1.8} />}
                Salvează și actualizează previzualizarea
              </button>
              <button
                type="button"
                onClick={reset}
                disabled={!!busy}
                data-testid="newsletter-reset-btn"
                className="inline-flex items-center gap-2 rounded-full border border-[color-mix(in_srgb,var(--milk)_20%,transparent)] px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-[var(--milk)] disabled:opacity-50"
              >
                <RotateCcw className="h-3.5 w-3.5" strokeWidth={1.8} /> Textele implicite
              </button>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78">Așa va arăta în inbox</p>
            <p className="mt-2 rounded-xl bg-[#2B1B13] px-4 py-3 text-sm ring-1 ring-[color-mix(in_srgb,var(--milk)_18%,transparent)]" data-testid="newsletter-subject-preview">
              <span className="text-[#F7F1E8]/68">Subiect:</span> <strong>{data.subject}</strong>
            </p>
            <iframe
              title="Previzualizare newsletter"
              srcDoc={data.html}
              data-testid="newsletter-preview-frame"
              className="mt-3 h-[900px] w-full rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#0D0907]"
            />
          </div>
        </div>
      )}
    </main>
  );
};

export default AdminNewsletterPage;
